import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { 
  Box, 
  IconButton,
  InputBase,
  Button,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import SwitchButtonToggle from './switchButtonToggle';

import priorityOptions from '../../../../constants/priorityOptions';
import DepartmentObject from '../../../../interfaces/departmentObject';
import FilterComponent from '../../../common/filterComponent';

interface ResetButtonForPopoverProps {
  resetOptionMethod: () => void;
}

interface ButtonWithPopoverProps {
  onOptionSelect: (selectedOption: number | null) => void;
  onDateOptionSelect: (selectedOption: string) => void;
  onDepartmentOptionSelect: (selectedOption: string) => void;
  departmentList: DepartmentObject[];
  setToggleAssignee: React.Dispatch<React.SetStateAction<boolean>>;
  toggleAssignee: boolean;
  setReportsFollowed: React.Dispatch<React.SetStateAction<boolean>>;
  reportsFollowed: boolean;
  setTag: React.Dispatch<React.SetStateAction<string>>;
}

const ResetButtonForPopover = ({
  resetOptionMethod,
}: ResetButtonForPopoverProps) => (
  <Button
    onClick={(event) => {
      resetOptionMethod();
      event.stopPropagation();
    }}
    sx={{
      marginLeft: '8px',
      marginRight: '5px',
      padding: 0,
      minWidth: 0,
    }}
  >
    <CloseIcon
      sx={{
        padding: 0,
        fontSize: 'small',
        stroke: '#000000',
        strokeWidth: '1.5px',
      }}
    />
  </Button>
);

const FilterButtonWithPopOver = ({
  onOptionSelect,
  onDateOptionSelect,
  onDepartmentOptionSelect,
  setToggleAssignee,
  departmentList,
  toggleAssignee,
  setReportsFollowed,
  reportsFollowed,
  setTag,
}: ButtonWithPopoverProps) => {
  // state for priority
  const [priorityAnchorEl, setPriorityAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [priorityIconRotation, setPriorityIconRotation] = useState<number>(0);
  const [prioritySelectedOption, setSelectedPriorityOption] = useState<
  number | string
  >(4);

  // state for date
  const [dateAnchorEl, setDateAnchorEl] = useState<null | HTMLElement>(null);
  const [dateIconRotation, setDateIconRotation] = useState<number>(0);
  const [selectedDateOption, setSelectedDateOption] = useState<string>('');

  // state for department
  const [departmentAnchorEl, setDepartmentAnchorEl] =
    useState<null | HTMLElement>(null);
  const [departmentIconRotation, setDepartmentIconRotation] =
    useState<number>(0);
  const [selectedDepartmentOption, setSelectedDepartmentOption] =
    useState<string>('Default');
  
  const [tagSearch, setTagSeach] = useState<string>('');

  const handleDateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDateAnchorEl(event.currentTarget);
    setDateIconRotation(dateIconRotation === 0 ? 180 : 0);
  };

  const handleDateClose = () => {
    setDateAnchorEl(null);
    setDateIconRotation(0);
  };

  const handleDateOptionClick = (option: string) => {
    setSelectedDateOption(option);
    onDateOptionSelect(option);
    handleDateClose();
  };

  const handlePriorityClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPriorityAnchorEl(event.currentTarget);
    setPriorityIconRotation(priorityIconRotation === 0 ? 180 : 0);
  };

  const handlePriorityClose = () => {
    setPriorityAnchorEl(null);
    setPriorityIconRotation(0);
  };

  const handlePriorityOptionClick = (option: number) => {
    setSelectedPriorityOption(option);
    onOptionSelect(option);
    handlePriorityClose();
  };

  const handleDepartmentClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setDepartmentAnchorEl(event.currentTarget);
    setDepartmentIconRotation(dateIconRotation === 0 ? 180 : 0);
  };

  const handleDepartmentClose = () => {
    setDepartmentAnchorEl(null);
    setDepartmentIconRotation(0);
  };

  const handleDepartmentOptionClick = (option: string) => {
    setSelectedDepartmentOption(option);
    onDepartmentOptionSelect(option);
    handleDepartmentClose();
  };

  const handleToggleSwitch = (newState: boolean) => {
    setToggleAssignee(newState);
  };

  const handleReportsFollowedSwitch = (newState: boolean) => {
    setReportsFollowed(newState);
  };

  const handleTagOnSubmit = () => {
    setTag(tagSearch);
  };

  const resetTag = () => {
    setTag('');
    setTagSeach('');
  };

  const resetEnabledFilterOption = () => {
    setReportsFollowed(false);
    setToggleAssignee(false);
  };

  const resetDepartmentOption = () => {
    setSelectedDepartmentOption('Default');
    onDepartmentOptionSelect('');
  };

  const resetPriorityOption = () => {
    setSelectedPriorityOption(4);
    onOptionSelect(null);
  };

  const resetDateOption = () => {
    setSelectedDateOption('');
    onDateOptionSelect('');
  };

  const priorityOpen = Boolean(priorityAnchorEl);
  const dateOpen = Boolean(dateAnchorEl);
  const departmentOpen = Boolean(departmentAnchorEl);

  const options = [3, 2, 1, 0];
  const dateOptions = [
    { id: 1, tag: 'created_at', label: 'Earliest' },
    { id: 2, tag: '', label: 'Latest' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      <FilterComponent 
        listOfFilter={
          [toggleAssignee, reportsFollowed]
        }
        resetEnabledFilterOption={resetEnabledFilterOption}
      >
        <Box
          sx={{
            cursor: 'pointer',
            '& :hover': {
              backgroundColor: '#F0F0F0',
            },
          }}
          component='div'
          onClick={() => handleToggleSwitch(!toggleAssignee)}
        >
          <Box
            sx={{
              display: 'flex',
              p: 1.5,
            }}
          >
            <SwitchButtonToggle
              onToggle={handleToggleSwitch}
              toggleSwitchState={toggleAssignee}
            />
            <Typography
              sx={{
                color: 'var(--grey-white-background, #878787)',
                textAlign: 'center',
                fontFamily: 'Schibsted Grotesk',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
              }}
            >
              <FormattedMessage defaultMessage='My Reports' />
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            cursor: 'pointer',
            '& :hover': {
              backgroundColor: '#F0F0F0',
            },
          }}
          component='div'
          onClick={() => handleReportsFollowedSwitch(!reportsFollowed)}
        >
          <Box
            sx={{
              display: 'flex',
              p: 1.5,
            }}
          >
            <SwitchButtonToggle
              onToggle={handleReportsFollowedSwitch}
              toggleSwitchState={reportsFollowed}
            />
            <Typography
              sx={{
                color: 'var(--grey-white-background, #878787)',
                textAlign: 'center',
                fontFamily: 'Schibsted Grotesk',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
              }}
            >
              <FormattedMessage defaultMessage='Reports Followed' />
            </Typography>
          </Box>
        </Box>
      </FilterComponent>

      <Button
        variant='outlined'
        sx={{
          borderRadius: '6px',
          border: departmentOpen ? '1px solid #181818' : '1px solid #D1D1D1',
          backgroundColor: 'white',
          padding: '5px 10px',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          margin: '0px 4px 0px 4px',
          '&:hover': {
            border: '1px solid #181818',
          },
        }}
        onClick={handleDepartmentClick}
        endIcon={
          selectedDepartmentOption === 'Default' ? (
            <ExpandMoreIcon
              style={{
                color: '#878787',
                marginLeft: '8px',
                padding: '0',
                transform: `rotate(${departmentIconRotation}deg)`,
              }}
              fontSize='small'
            />
          ) : (
            <ResetButtonForPopover resetOptionMethod={resetDepartmentOption} />
          )
        }
      >
        <Typography
          sx={{
            color: 'var(--grey-white-background, #878787)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            paddingRight: '10px',
            textTransform: 'none',
          }}
        >
          <FormattedMessage defaultMessage='Department:' />
        </Typography>
        <Typography
          sx={{
            color: 'var(--Black, #181818)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            textTransform: 'none',
          }}
        >
          {selectedDepartmentOption}
        </Typography>
      </Button>

      <Popover
        sx={{ marginTop: '5px' }}
        open={departmentOpen}
        anchorEl={departmentAnchorEl}
        onClose={handleDepartmentClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '246px',
            maxHeight: '400px',
            overflow: 'auto',
            borderRadius: '6px',
            backgroundColor: '#FFF',
          }}
        >
          {departmentList.map((item, index) => (
            <Button
              sx={{
                width: '100%',
                margin: '0',
                color: '#000',
                fontFamily: 'Schibsted Grotesk',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textTransform: 'none',
                transition: 'background-color 0.3s',
                p: '7px 17px 7px 17px',
                textAlign: 'left',
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: '#F0F0F0',
                },
              }}
              key={index}
              onClick={() => handleDepartmentOptionClick(item.name)}
            >
              {item.name}
            </Button>
          ))}
        </div>
      </Popover>

      <Button
        variant='outlined'
        sx={{
          borderRadius: '6px',
          border: priorityOpen ? '1px solid #181818' : '1px solid #D1D1D1',
          backgroundColor: 'white',
          padding: '5px 10px',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          margin: '0px 4px 0px 4px',
          '&:hover': {
            border: '1px solid #181818',
          },
        }}
        onClick={handlePriorityClick}
        endIcon={
          prioritySelectedOption === 4 ? (
            <ExpandMoreIcon
              style={{
                color: '#878787',
                marginLeft: '8px',
                padding: '0',
                transform: `rotate(${priorityIconRotation}deg)`,
              }}
              fontSize='small'
            />
          ) : (
            <ResetButtonForPopover resetOptionMethod={resetPriorityOption} />
          )
        }
      >
        <Typography
          sx={{
            color: 'var(--grey-white-background, #878787)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            paddingRight: '10px',
            textTransform: 'none',
          }}
        >
          <FormattedMessage defaultMessage='Priority:' />
        </Typography>
        <Typography
          sx={{
            color: 'var(--Black, #181818)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            textTransform: 'none',
          }}
        >
          {
            priorityOptions.find(
              (status) => status.id === prioritySelectedOption,
            )?.name
          }
        </Typography>
      </Button>

      <Popover
        sx={{ marginTop: '5px' }}
        open={priorityOpen}
        anchorEl={priorityAnchorEl}
        onClose={handlePriorityClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '246px',
            maxHeight: '400px',
            overflow: 'auto',
            borderRadius: '6px',
            backgroundColor: '#FFF',
          }}
        >
          {options.map((item, index) => (
            <Button
              sx={{
                width: '100%',
                margin: '0',
                color: '#000',
                fontFamily: 'Schibsted Grotesk',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textTransform: 'none',
                transition: 'background-color 0.3s',
                p: '2px 10px 2px 10px',
                textAlign: 'left',
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: '#F0F0F0',
                },
              }}
              key={index}
              onClick={() => handlePriorityOptionClick(item)}
            >
              <Stack
                sx={{
                  '&::before': {
                    content: `url(${priorityOptions.find(
                      (status) => status.id === item,
                    )?.color})`,
                    display: 'block',
                    marginRight: '10px',
                    backgroundSize: 'cover',
                  },
                }}
              />
              {priorityOptions.find((status) => status.id === item)?.name}
            </Button>
          ))}
        </div>
      </Popover>

      <Button
        variant='outlined'
        sx={{
          borderRadius: '6px',
          border: dateOpen ? '1px solid #181818' : '1px solid #D1D1D1',
          backgroundColor: 'white',
          padding: '5px 10px',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          margin: '0px 4px 0px 4px',
          '&:hover': {
            border: '1px solid #181818',
          },
        }}
        onClick={handleDateClick}
        endIcon={
          !selectedDateOption ? (
            <ExpandMoreIcon
              style={{
                color: '#878787',
                marginLeft: '8px',
                padding: '0',
                transform: `rotate(${dateIconRotation}deg)`,
              }}
              fontSize='small'
            />
          ) : (
            <ResetButtonForPopover resetOptionMethod={resetDateOption} />
          )
        }
      >
        <Typography
          sx={{
            color: 'var(--grey-white-background, #878787)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            paddingRight: '10px',
            textTransform: 'none',
          }}
        >
          <FormattedMessage defaultMessage='Date:' />
        </Typography>
        <Typography
          sx={{
            color: 'var(--Black, #181818)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            textTransform: 'none',
          }}
        >
          {
            dateOptions.find((status) => status.tag === selectedDateOption)
              ?.label
          }
        </Typography>
      </Button>

      <Popover
        sx={{ marginTop: '5px' }}
        open={dateOpen}
        anchorEl={dateAnchorEl}
        onClose={handleDateClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '246px',
            maxHeight: '400px',
            overflow: 'auto',
            borderRadius: '6px',
            backgroundColor: '#FFF',
          }}
        >
          {dateOptions.map((item, index) => (
            <Button
              sx={{
                width: '100%',
                margin: '0',
                color: '#000',
                fontFamily: 'Schibsted Grotesk',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textTransform: 'none',
                transition: 'background-color 0.3s',
                p: '7px 17px 7px 17px',
                textAlign: 'left',
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: '#F0F0F0',
                },
              }}
              key={index}
              onClick={() => handleDateOptionClick(item.tag)}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </Popover>
      
      <Box
        component='form'
        onSubmit={(event: React.ChangeEvent<HTMLInputElement>) => {
          event.preventDefault();
          handleTagOnSubmit();
        }}
        sx={{
          border: tagSearch ? '1px solid #181818' : '1px solid #D1D1D1',
          borderRadius: '6px',
          margin: '0px 4px 0px 4px',
          '&:hover': {
            border: '1px solid #181818',
          },
        }}
      >
        <IconButton sx={{ p: '10px', ml: 1 }} aria-label=" menu" onClick={handleTagOnSubmit}>
          <SearchIcon />
        </IconButton>
        <InputBase
          value={tagSearch}
          placeholder="Search Tags"
          sx={{ display: 'inline-flex', width: '120px' }}
          onChange={
            (event: React.ChangeEvent<HTMLInputElement>) => 
              setTagSeach(event.target.value)
          }
        />
        {tagSearch && <ResetButtonForPopover resetOptionMethod={resetTag} />}
      </Box>
    </div>
  );
};

export default FilterButtonWithPopOver;
